/**
 * Turn the Admiralty UK Tidal API's discrete HW/LW events into the same
 * TideHeightState shape the height card uses: the bracketing previous/next event,
 * the height now (a cosine interpolation between them, the standard tidal-curve
 * shape, flat at the slack of HW and LW), and a spring/neap proxy from the day's
 * ranges. The free Discovery tier returns events only (no interval curve), so the
 * cosine fill is our curve between the official HW/LW points.
 */
import type { AdmiraltyEvent } from './admiralty';
import type { TideHeightState, HeightEvent, SpringLabel } from './heights';
import { eventsHeightAt } from './curve';

function parseMs(iso: string): number {
  // Admiralty DateTimes are UTC; add Z if the string carries no zone.
  const hasZone = /[zZ]|[+-]\d\d:?\d\d$/.test(iso);
  return Date.parse(hasZone ? iso : `${iso}Z`);
}

function toHeightEvent(e: AdmiraltyEvent): HeightEvent {
  return { type: e.EventType === 'HighWater' ? 'H' : 'L', t: parseMs(e.DateTime), heightM: e.Height };
}

/** A TideHeightState at `ms` from a station's HW/LW events, or null if too few. */
export function admiraltyHeightState(events: AdmiraltyEvent[], ms: number): TideHeightState | null {
  const evs = events
    .map(toHeightEvent)
    .filter((e) => Number.isFinite(e.t))
    .sort((a, b) => a.t - b.t);
  if (evs.length < 2) return null;

  let prev = evs[0];
  let next = evs[1];
  for (let i = 0; i + 1 < evs.length; i++) {
    if (evs[i].t <= ms && ms <= evs[i + 1].t) {
      prev = evs[i];
      next = evs[i + 1];
      break;
    }
    if (ms > evs[i + 1].t) {
      prev = evs[evs.length - 2];
      next = evs[evs.length - 1];
    }
  }

  // Height now: cosine fill between the bracketing official HW/LW (shared maths).
  const heightM = eventsHeightAt(evs, ms);
  const rising = next.type === 'H';

  let maxRange = 0.01;
  for (let i = 0; i + 1 < evs.length; i++) maxRange = Math.max(maxRange, Math.abs(evs[i + 1].heightM - evs[i].heightM));
  const springRatio = Math.min(1, Math.abs(next.heightM - prev.heightM) / maxRange);
  const springLabel: SpringLabel = springRatio >= 0.66 ? 'Springs' : springRatio <= 0.33 ? 'Neaps' : 'Mid';

  return { heightM, rising, prev, next, springRatio, springLabel };
}
